import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, Segmented, Slider, Switch, Row, Col, Statistic, Typography, Select, Space, Tag, theme,
} from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import { PFMode, phaseAngle, sampleWave, waveStats, MODE_COPY } from './waveforms';

const TWO_PI = 2 * Math.PI;

/* Slides the waveform window while the element is on screen. Paused when the tab is hidden
   (ProjectHub keeps visited modules mounted with display:none) and for reduced-motion users. */
function useWaveOffset(enabled: boolean, ref: React.RefObject<Element>) {
  const [offset, setOffset] = useState(0);
  const [visible, setVisible] = useState(true);
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);

  useEffect(() => {
    if (!enabled || !visible || reduced) return;
    let raf = 0, last = performance.now();
    const tick = (now: number) => {
      setOffset(o => (o + ((now - last) / 1000) * Math.PI) % TWO_PI); // half a cycle per second
      last = now;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [enabled, visible, reduced]);

  return enabled && !reduced ? offset : 0;
}

// ─── Waveform ──────────────────────────────────────────────────────────────

interface WaveformProps {
  phi: number;              // signed: + lagging, − leading
  cycles?: number;
  height?: number;
  offset?: number;
  showPower?: boolean;
  compact?: boolean;
  label?: string;
}

export const PFWaveform: React.FC<WaveformProps> = ({
  phi, cycles = 2, height = 200, offset = 0, showPower = true, compact = false, label,
}) => {
  const { token } = theme.useToken();
  /* A header strip holds the label / φ bracket so text never sits on the curves. Current is drawn
     at 80% amplitude (the usual teaching convention) so it stays distinguishable from voltage at
     unity, where the two would otherwise overlap exactly. Purely visual — stats use unit amplitudes. */
  const W = 600, H = height, top = compact ? 22 : 30, bottom = 8;
  const amp = (H - top - bottom) / 2, mid = top + amp;
  const IA = 0.8;
  const span = cycles * TWO_PI;
  const pts = useMemo(() => sampleWave(phi, cycles, compact ? 160 : 300, offset), [phi, cycles, compact, offset]);
  const x = (theta: number) => ((theta - offset) / span) * W;
  const y = (val: number) => mid - val * amp;

  const line = (key: 'v' | 'i') => pts.map((p, k) =>
    `${k ? 'L' : 'M'}${x(p.theta).toFixed(1)},${y(key === 'i' ? p.i * IA : p.v).toFixed(1)}`).join('');
  const area = (sign: 1 | -1) =>
    `M${x(pts[0].theta)},${mid}` +
    pts.map(p => `L${x(p.theta).toFixed(1)},${y(IA * (sign > 0 ? Math.max(p.p, 0) : Math.min(p.p, 0))).toFixed(1)}`).join('') +
    `L${x(pts[pts.length - 1].theta)},${mid}Z`;

  // First voltage peak far enough in that the current peak is on screen too.
  const lead = Math.max(-phi, 0);
  let vPeak = Math.PI / 2;
  while (vPeak < offset + lead + 0.05) vPeak += TWO_PI;
  const iPeak = vPeak + phi;
  const showMarker = Math.abs(phi) > 0.02 && x(Math.max(vPeak, iPeak)) < W - 10;
  const deg = Math.abs((phi * 180) / Math.PI).toFixed(1);
  const mode = phi > 0.0005 ? 'lag' : phi < -0.0005 ? 'lead' : 'in phase';
  const current = token.colorInfo;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ display: 'block' }}
      aria-label={`${label ? `${label}: ` : ''}voltage and current waveforms, current ${mode}${showMarker ? ` by ${deg} degrees` : ''}`}>
      <line x1={0} y1={mid} x2={W} y2={mid} stroke={token.colorBorder} strokeWidth={1} />
      {showPower && (
        <>
          <path d={area(1)} fill={token.colorSuccess} fillOpacity={0.22} />
          <path d={area(-1)} fill={token.colorError} fillOpacity={0.32} />
        </>
      )}
      <path d={line('v')} fill="none" stroke={token.colorText} strokeWidth={compact ? 1.5 : 2.2} />
      <path d={line('i')} fill="none" stroke={current} strokeWidth={compact ? 1.5 : 2.2} />
      {showMarker && (
        <g>
          <line x1={x(vPeak)} y1={compact ? y(1) : top - 4} x2={x(vPeak)} y2={mid + 6} stroke={token.colorText} strokeDasharray="3 3" />
          <line x1={x(iPeak)} y1={compact ? y(IA) : top - 4} x2={x(iPeak)} y2={mid + 6} stroke={current} strokeDasharray="3 3" />
          {!compact && (
            <>
              <line x1={x(Math.min(vPeak, iPeak))} y1={top - 4} x2={x(Math.max(vPeak, iPeak))} y2={top - 4}
                stroke={token.colorTextSecondary} strokeWidth={1.5} />
              <text x={(x(vPeak) + x(iPeak)) / 2} y={top - 10} textAnchor="middle" fontSize={13} fill={token.colorTextSecondary}>
                φ {deg}° {mode}
              </text>
            </>
          )}
        </g>
      )}
      {label && <text x={4} y={15} fontSize={compact ? 15 : 13} fontWeight={600} fill={token.colorTextSecondary}>{label}</text>}
    </svg>
  );
};

export const WaveLegend: React.FC<{ showPower?: boolean }> = ({ showPower = true }) => {
  const { token } = theme.useToken();
  const key = (color: string, text: string, fill = false) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: token.colorTextSecondary }}>
      <span style={fill
        ? { width: 14, height: 10, background: color, opacity: 0.5, borderRadius: 2 }
        : { width: 16, height: 3, background: color, borderRadius: 2 }} />
      {text}
    </span>
  );
  return (
    <Space size={14} wrap>
      {key(token.colorText, 'Voltage')}
      {key(token.colorInfo, 'Current')}
      {showPower && key(token.colorSuccess, 'Power delivered', true)}
      {showPower && key(token.colorError, 'Power returned to source', true)}
    </Space>
  );
};

// ─── Phasor ────────────────────────────────────────────────────────────────

export const PFPhasor: React.FC<{ phi: number; offset?: number; size?: number }> = ({ phi, offset = 0, size = 180 }) => {
  const { token } = theme.useToken();
  const c = size / 2, r = size * 0.38;
  // Phasors rotate counter-clockwise; screen y points down, so negate the sine.
  const tip = (angle: number, len = r) => [c + len * Math.cos(angle), c - len * Math.sin(angle)];
  const vA = offset, iA = offset - phi;
  const [vx, vy] = tip(vA), [ix, iy] = tip(iA, r * 0.82);
  const arcR = r * 0.32;
  const [ax0, ay0] = tip(vA, arcR), [ax1, ay1] = tip(iA, arcR);
  const sweep = phi > 0 ? 1 : 0;
  const arrow = (x2: number, y2: number, color: string, id: string) => (
    <>
      <defs>
        <marker id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill={color} />
        </marker>
      </defs>
      <line x1={c} y1={c} x2={x2} y2={y2} stroke={color} strokeWidth={2.5} markerEnd={`url(#${id})`} />
    </>
  );
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: size, display: 'block', margin: '0 auto' }}
      role="img" aria-label={`Phasor diagram, current ${phi > 0 ? 'lagging' : phi < 0 ? 'leading' : 'in phase with'} voltage`}>
      <circle cx={c} cy={c} r={r} fill="none" stroke={token.colorBorderSecondary} />
      <line x1={c - r} y1={c} x2={c + r} y2={c} stroke={token.colorBorderSecondary} />
      <line x1={c} y1={c - r} x2={c} y2={c + r} stroke={token.colorBorderSecondary} />
      {Math.abs(phi) > 0.02 && (
        <path d={`M${ax0},${ay0} A${arcR},${arcR} 0 0 ${sweep} ${ax1},${ay1}`} fill="none" stroke={token.colorTextSecondary} strokeWidth={1.5} />
      )}
      {arrow(vx, vy, token.colorText, 'pf-ph-v')}
      {arrow(ix, iy, token.colorInfo, 'pf-ph-i')}
      {/* Labels sit just past each tip, nudged to opposite sides of the arrow so V and I never
          collide when the phasors are close together (near unity). */}
      {(() => {
        const label = (angle: number, len: number, side: 1 | -1, text: string, color: string) => {
          const [tx, ty] = tip(angle, len + 12);
          const nx = -Math.sin(angle) * 9 * side, ny = -Math.cos(angle) * 9 * side;
          return <text x={tx + nx} y={ty + ny} textAnchor="middle" dominantBaseline="middle" fontSize={12} fontWeight={600} fill={color}>{text}</text>;
        };
        const side: 1 | -1 = phi >= 0 ? 1 : -1;
        return <>{label(vA, r, side, 'V', token.colorText)}{label(iA, r * 0.82, side === 1 ? -1 : 1, 'I', token.colorInfo)}</>;
      })()}
    </svg>
  );
};

// ─── Explorer card ─────────────────────────────────────────────────────────

export interface ExplorerMotor { id: string; label: string; pf: number; }

export const WaveformExplorer: React.FC<{ motors?: ExplorerMotor[] }> = ({ motors = [] }) => {
  const { token } = theme.useToken();
  const [mode, setMode] = useState<PFMode>('lagging');
  const [pf, setPf] = useState(0.7);
  const [animate, setAnimate] = useState(true);
  const [motorId, setMotorId] = useState<string | undefined>();
  const ref = useRef<HTMLDivElement>(null);
  const offset = useWaveOffset(animate, ref);

  const phi = phaseAngle(pf, mode);
  const stats = waveStats(phi);
  const copy = MODE_COPY[mode];

  const pickMotor = (id?: string) => {
    setMotorId(id);
    const m = motors.find(mm => mm.id === id);
    if (m) { setMode(m.pf >= 0.9995 ? 'unity' : 'lagging'); setPf(m.pf); }
  };

  return (
    <Card
      title={<span><LineChartOutlined /> Waveform explorer</span>}
      extra={<Space size={6}><Typography.Text type="secondary" style={{ fontSize: 12 }}>Animate</Typography.Text><Switch size="small" checked={animate} onChange={setAnimate} /></Space>}
      style={{ marginBottom: 16 }}
    >
      <div ref={ref}>
        <Row gutter={[16, 12]} align="middle" style={{ marginBottom: 8 }}>
          <Col xs={24} md={9}>
            <Segmented block value={mode} onChange={v => { setMode(v as PFMode); setMotorId(undefined); }}
              options={[{ label: 'Unity', value: 'unity' }, { label: 'Lagging', value: 'lagging' }, { label: 'Leading', value: 'leading' }]} />
          </Col>
          <Col xs={24} md={9}>
            <Space.Compact block>
              <span style={{ fontSize: 13, whiteSpace: 'nowrap', alignSelf: 'center', marginRight: 10 }}>PF {mode === 'unity' ? '1.00' : pf.toFixed(2)}</span>
              <Slider style={{ flex: 1, margin: '0 6px' }} min={0.2} max={0.99} step={0.01} value={pf} disabled={mode === 'unity'}
                onChange={v => { setPf(v as number); setMotorId(undefined); }} aria-label="Power factor" />
            </Space.Compact>
          </Col>
          <Col xs={24} md={6}>
            <Select allowClear placeholder="Load a motor's as-found PF" style={{ width: '100%' }} value={motorId}
              disabled={motors.length === 0} onChange={pickMotor}
              options={motors.map(m => ({ value: m.id, label: `${m.label} — PF ${m.pf.toFixed(2)}` }))} />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={17}>
            <PFWaveform phi={phi} offset={offset} />
            <div style={{ marginTop: 6 }}><WaveLegend /></div>
          </Col>
          <Col xs={24} lg={7}>
            <PFPhasor phi={phi} offset={offset} />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
          <Col xs={12} md={6}><Statistic title="Phase angle φ" value={`${Math.abs(stats.phiDeg).toFixed(1)}°`} suffix={<Typography.Text type="secondary" style={{ fontSize: 13 }}>{mode === 'unity' ? '' : mode === 'lagging' ? 'lag' : 'lead'}</Typography.Text>} /></Col>
          <Col xs={12} md={6}><Statistic title="Power factor (cos φ)" value={stats.pf.toFixed(2)} /></Col>
          <Col xs={12} md={6}><Statistic title="Energy returned each cycle" value={`${stats.returnedEnergyPct.toFixed(1)}%`} valueStyle={{ color: stats.returnedEnergyPct > 0.05 ? token.colorError : undefined }} /></Col>
          <Col xs={12} md={6}><Statistic title="Time power flows backward" value={`${stats.negativeTimePct.toFixed(1)}%`} /></Col>
        </Row>

        <div style={{ marginTop: 14, padding: '10px 14px', background: token.colorFillQuaternary, borderRadius: token.borderRadius }}>
          <Space size={8} wrap style={{ marginBottom: 4 }}>
            <Typography.Text strong>{copy.title}</Typography.Text>
            <Tag color={mode === 'unity' ? 'green' : mode === 'lagging' ? 'orange' : 'blue'} style={{ marginInlineEnd: 0 }}>{copy.caption}</Tag>
          </Space>
          <Typography.Paragraph type="secondary" style={{ margin: 0 }}>{copy.detail}</Typography.Paragraph>
        </div>
      </div>
    </Card>
  );
};
